import type { KnowledgeScope } from '../constants/knowledgeScope'

export type SelectedAttribute = { key: string; value: string }

export function formatAttrKv(attr: SelectedAttribute): string {
  return `${attr.key}=${attr.value}`
}

export function parseAttrKv(raw: string): SelectedAttribute | null {
  const s = (raw || '').trim()
  const eq = s.indexOf('=')
  if (eq <= 0) return null
  const key = s.slice(0, eq).trim()
  const value = s.slice(eq + 1).trim()
  if (!key || !value) return null
  return { key, value }
}

export function attrKvKey(raw: string): string {
  return formatAttrKv(parseAttrKv(raw) || { key: raw, value: '' })
}

export function dedupeAttributes(attrs: SelectedAttribute[]): SelectedAttribute[] {
  const seen = new Set<string>()
  const out: SelectedAttribute[] = []
  for (const a of attrs) {
    const key = formatAttrKv(a)
    if (seen.has(key)) continue
    seen.add(key)
    out.push(a)
  }
  return out
}

export function toggleAttribute(
  selected: SelectedAttribute[],
  attr: SelectedAttribute,
): SelectedAttribute[] {
  const id = formatAttrKv(attr)
  const exists = selected.some((s) => formatAttrKv(s) === id)
  if (exists) return selected.filter((s) => formatAttrKv(s) !== id)
  return dedupeAttributes([...selected, attr])
}

export function isAttributeSelected(selected: SelectedAttribute[], attr: SelectedAttribute): boolean {
  const id = formatAttrKv(attr)
  return selected.some((s) => formatAttrKv(s) === id)
}

export function buildAttributeSearchPath(
  scope: KnowledgeScope,
  attrs: SelectedAttribute[],
  options?: { excludeItemId?: number },
): string {
  const params = new URLSearchParams({ scope, attr_search: '1' })
  for (const a of dedupeAttributes(attrs)) {
    params.append('attr', formatAttrKv(a))
  }
  const ex = Number(options?.excludeItemId ?? 0)
  if (Number.isFinite(ex) && ex > 0) params.set('from_item', String(ex))
  return `/search?${params.toString()}`
}

export function parseAttributesFromSearchParams(
  params: URLSearchParams,
): { attrSearch: boolean; attributes: SelectedAttribute[]; fromItemId: number } {
  const attrSearch = params.get('attr_search') === '1'
  const attributes = dedupeAttributes(
    params
      .getAll('attr')
      .map(parseAttrKv)
      .filter((x): x is SelectedAttribute => x != null),
  )
  const fromItemId = Number(params.get('from_item') || 0)
  return {
    attrSearch,
    attributes,
    fromItemId: Number.isFinite(fromItemId) ? fromItemId : 0,
  }
}
